/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React from "react";
import {
  Modal,
  ModalBody,
  ModalButton,
  ModalFooter,
  ModalHeader,
} from "baseui/modal";

import { TOP_ITEM_ZINDEX } from "../Utils/SiteProps";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { StyledBody } from "baseui/card";

export const DeleteConfirmationModal = ({
  isOpen,
  header,
  bodyText,
  onDelete,
  onCancel,
  confirmLabel = "Delete",
}: {
  isOpen: boolean;
  header: string;
  bodyText: string;
  onDelete: () => void;
  onCancel: () => void;
  /** Text on the red confirm button, e.g. "Clear" or "Start New Mapping". */
  confirmLabel?: string;
}): JSX.Element => {
  const { currentTheme } = useDexterTheme();

  return (
    <Modal
      overrides={{
        Root: {
          style: {
            zIndex: TOP_ITEM_ZINDEX,
          },
        },
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
          },
        },
      }}
      isOpen={isOpen}
      onClose={onCancel}
    >
      <ModalHeader
        style={{
          backgroundColor: currentTheme.colors.pageBackgroundColor,
          color: currentTheme.colors.defaultTextColor,
          fontFamily: currentTheme.fonts.primary,
          fontSize: currentTheme.fontSizes.large,
        }}
      >
        {header}
      </ModalHeader>
      <ModalBody>
        <StyledBody>
          <p
            style={{
              fontFamily: currentTheme.fonts.primary,
              fontSize: currentTheme.fontSizes.medium,
              color: currentTheme.colors.defaultTextColor,
            }}
          >
            {bodyText}
          </p>
        </StyledBody>
      </ModalBody>
      <ModalFooter>
        <ModalButton
          kind={"tertiary"}
          onClick={onCancel}
          overrides={{
            BaseButton: {
              style: {
                fontFamily: currentTheme.fonts.primary,
                fontSize: currentTheme.fontSizes.medium,
                backgroundColor: "transparent",
                color: currentTheme.colors.text,
              },
            },
          }}
        >
          Cancel
        </ModalButton>
        <ModalButton
          data-testid="modal-delete-button"
          kind={"primary"}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: currentTheme.colors.negative,
                color: currentTheme.colors.primaryElementFontColor,
              },
            },
          }}
          onClick={onDelete}
        >
          {confirmLabel}
        </ModalButton>
      </ModalFooter>
    </Modal>
  );
};
